/**
 * Shared Video Express Max create helpers — the single source of truth for the
 * campaign create payload, the post-create sync payload, and server-error
 * unwrapping. Used by the single Video Express page
 * (`video-express-campaigns.tsx`), which fans each selected style out into its
 * own campaign via the same payload builders so launches never drift.
 */

export type VideoExpressAngleMode = "saved" | "custom" | "generate";

/**
 * The complete set of per-campaign inputs the payload builders read. The single
 * wizard and each batch row both collapse their local state into this shape.
 */
export type VideoExpressInput = {
  name: string;
  personaId: string | null;
  /**
   * A campaign promotes EITHER a product OR an offer. `promotedItemType`
   * discriminates; exactly one of `productId` / `offeringId` is set.
   */
  promotedItemType: "product" | "offering";
  productId: string | null;
  offeringId: string | null;
  angleMode: VideoExpressAngleMode;
  narrativeAngleId: string | null;
  concept: string;
  selectedCharacterIds: string[];
  videoStyle: string;
  storyCategory: string;
  /** Total ad length in seconds (36 / 72 / 108). */
  clipTotalSeconds: number;
};

/**
 * `apiRequest` throws errors shaped like `"500: {\"message\":\"...\"}"` (the
 * HTTP status followed by the raw response body). Unwrap the real server
 * `message` so toasts show the actual failure reason, not a generic line.
 */
export function extractServerError(e: any): string {
  const raw = (e?.message ?? "").toString().trim();
  if (!raw) return "Something went wrong. Please try again.";
  const m = raw.match(/^\d{3}:\s*([\s\S]*)$/);
  const body = (m ? m[1] : raw).trim();
  try {
    const parsed = JSON.parse(body);
    if (parsed && typeof parsed.message === "string" && parsed.message.trim()) {
      return parsed.message.trim();
    }
  } catch {
    /* not JSON — fall through to the raw body */
  }
  return body || raw;
}

/**
 * The full create payload POSTed to `/api/brands/:brandId/video-express-campaigns`.
 * Only "saved" carries an angle id; "custom" sends its typed concept as
 * `customConcept` (transformed into a saved angle server-side, once); "generate"
 * leaves both null so the pipeline derives the creative direction on its own.
 */
export function buildVideoExpressPayload(input: VideoExpressInput) {
  const usingSaved = input.angleMode === "saved";
  const usingCustom = input.angleMode === "custom";
  const count = Math.max(1, input.selectedCharacterIds.length);
  const usingOffering = input.promotedItemType === "offering";
  return {
    name: input.name.trim(),
    adUnitTypeId: null,
    personaId: input.personaId || null,
    promotedItemType: input.promotedItemType,
    productId: usingOffering ? null : (input.productId || null),
    offeringId: usingOffering ? (input.offeringId || null) : null,
    narrativeAngleId: usingSaved ? (input.narrativeAngleId || null) : null,
    customConcept: usingCustom ? input.concept.trim() : null,
    videoStyle: input.videoStyle,
    storyCategory: input.storyCategory || null,
    duration: `${input.clipTotalSeconds}s`,
    secondsPerScene: Math.round(input.clipTotalSeconds / 9),
    videoCount: count,
    avatarCount: count,
    campaignMode: "oneshot",
    concept: usingCustom ? (input.concept.trim() || null) : null,
    targetAudience: null,
    notes: input.selectedCharacterIds.length > 0 ? { selectedCharacterIds: input.selectedCharacterIds } : null,
  };
}

/**
 * The subset of fields kept in sync after the initial create (the
 * flush-before-start path). Excludes the one-time (expensive) custom-concept
 * transform; the narrative angle is only synced for saved angles.
 */
export function buildVideoExpressSyncPayload(input: VideoExpressInput) {
  const usingCustom = input.angleMode === "custom";
  const count = Math.max(1, input.selectedCharacterIds.length);
  const usingOffering = input.promotedItemType === "offering";
  const p: any = {
    name: input.name.trim(),
    adUnitTypeId: null,
    personaId: input.personaId || null,
    promotedItemType: input.promotedItemType,
    productId: usingOffering ? null : (input.productId || null),
    offeringId: usingOffering ? (input.offeringId || null) : null,
    videoStyle: input.videoStyle,
    storyCategory: input.storyCategory || null,
    duration: `${input.clipTotalSeconds}s`,
    secondsPerScene: Math.round(input.clipTotalSeconds / 9),
    videoCount: count,
    avatarCount: count,
    notes: input.selectedCharacterIds.length > 0 ? { selectedCharacterIds: input.selectedCharacterIds } : null,
  };
  // Saved angles sync the chosen id; "generate" syncs null (no anchor). Custom
  // is omitted — its concept→angle transform runs once at create and is never
  // re-applied on edits.
  if (!usingCustom) p.narrativeAngleId = input.angleMode === "saved" ? (input.narrativeAngleId || null) : null;
  return p;
}
